/**
 * Blishka Dynamic SEO & Head Management
 * Ensures client-side route changes update Title, Meta Description,
 * Meta Keywords, Canonical Link, and Open Graph tags for optimal indexing.
 */

export interface PageSEOMetadata {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogImage?: string;
}

const BASE_URL = 'https://blishka.pages.dev';
const DEFAULT_IMAGE = `${BASE_URL}/brand-logo.png`;

const CATEGORY_SEO_MAP: Record<string, { title: string; description: string; keywords: string }> = {
  waxing: {
    title: 'Waxing at Home in Bhilai & Durg | Honey, White Chocolate & Rica Wax | Blishka',
    description: 'Painless doorstep waxing in Bhilai & Durg starting at ₹100. Honey wax, Rica Italian wax, white chocolate wax, and full body waxing by certified female beauticians with single-use strips.',
    keywords: 'Waxing at home Bhilai, Rica wax Durg, Full body waxing Bhilai, painless waxing at home, underarms honey wax Bhilai Durg'
  },
  facial: {
    title: 'Glow Facials at Home Bhilai & Durg | VLCC, Lotus & O3+ Bridal Facials | Blishka',
    description: 'Salon-grade glow facials at home in Bhilai & Durg. Aroma fruit, VLCC glow, Lotus brightening & O3+ bridal facial with rejuvenating facial massage.',
    keywords: 'Facial at home Bhilai, VLCC facial Durg, bridal glow facial Bhilai, O3+ facial at home, doorstep facial service Chhattisgarh'
  },
  cleanup: {
    title: 'Face Cleanup & De-Tan at Home Bhilai & Durg | Blishka Doorstep Parlour',
    description: 'Instant sun tan removal, deep pore cleansing, and herbal de-tan for face, arms, and back. Transparent prices from ₹199 in Bhilai & Durg.',
    keywords: 'Face cleanup Bhilai, De-tan at home Durg, instant glow cleanup, face bleach home service Bhilai'
  },
  threading: {
    title: 'Eyebrow & Facial Threading at Home Bhilai & Durg | Blishka',
    description: 'Gentle eyebrow shaping, upper lip, forehead, chin & full face threading at your doorstep in Bhilai & Durg starting at just ₹30.',
    keywords: 'Eyebrow threading Bhilai, doorstep threading Durg, full face threading at home, home parlour threading'
  },
  manicure: {
    title: 'Spa Manicure at Home Bhilai & Durg | Hand Scrub & Nail Care | Blishka',
    description: 'Relaxing spa manicure at home. Floral soak, hand scrub, cuticle care, and nourishing massage in Bhilai and Durg.',
    keywords: 'Manicure at home Bhilai, hand spa Durg, doorstep nail care, parlour manicure home visit'
  },
  pedicure: {
    title: 'Spa Pedicure at Home Bhilai & Durg | Herbal Foot Soak & Heel Care | Blishka',
    description: 'Pampering herbal foot soak, heel scrub, callus removal, and reflexology foot massage at your doorstep in Bhilai & Durg.',
    keywords: 'Pedicure at home Bhilai, foot spa Durg, heel crack treatment Bhilai, doorstep pedicure'
  },
  hairspa: {
    title: 'Hair Spa at Home Bhilai & Durg | Loreal & Keratin Conditioning | Blishka',
    description: "L'Oreal cream bath, steam, and deep conditioning scalp massage at home in Bhilai & Durg for smooth, frizz-free hair.",
    keywords: 'Hair spa at home Bhilai, Loreal hair spa Durg, keratin treatment at home Bhilai, scalp massage'
  },
  'relaxing-massage': {
    title: 'Relaxing Head, Back & Body Massage at Home Bhilai & Durg | Blishka',
    description: 'Rejuvenating head, shoulder, back, and full body massage at your doorstep in Bhilai and Durg by verified female specialists.',
    keywords: 'Head massage at home Bhilai, body massage for women Durg, back massage home visit, relaxation massage Bhilai'
  },
  scrubbing: {
    title: 'Body Polishing & Scrubbing at Home Bhilai & Durg | Blishka',
    description: 'Full body polishing and exfoliating botanical scrub for hands, legs, and back at home in Bhilai & Durg.',
    keywords: 'Body scrubbing Bhilai, body polish Durg, full body exfoliation, doorstep skin brightening'
  },
  'massage-scrub': {
    title: 'Massage & Scrubbing Combos at Home Bhilai & Durg | Blishka',
    description: 'Deep body exfoliation & relaxing herbal massage combinations delivered to your home in Bhilai and Durg.',
    keywords: 'Body massage and scrub Bhilai, doorstep parlour combos Durg, herbal full body polish'
  }
};

const BUNDLE_SEO_MAP: Record<string, { title: string; description: string }> = {
  'bundle-everyday': {
    title: 'Blishka Everyday Combo (₹499) | Eyebrow, Upper Lip & Cleanup at Home',
    description: 'Essential monthly grooming package at ₹499. Includes eyebrow threading, upper lip threading, and pore-cleansing cleanup at home in Bhilai & Durg.'
  },
  'bundle-glow': {
    title: 'Blishka Glow Package (₹599) | Cleanup, Threading & Face Massage',
    description: 'Radiant skin glow combo at ₹599. Includes deep cleanup, eyebrow, upper lip, and rejuvenating face massage in Bhilai and Durg.'
  },
  'bundle-wax-smooth': {
    title: 'Blishka Wax & Smooth Combo (₹699) | Full Arms, Legs & Underarms Waxing',
    description: 'Complete limb smooth package at ₹699. Full arms, full legs, and underarms waxing at home with soothing finish in Bhilai & Durg.'
  },
  'bundle-self-care': {
    title: 'Blishka Self-Care Pamper Combo (₹899) | Facial & Manicure at Home',
    description: 'Luxury self-care bundle at ₹899. Salon glow facial and spa manicure in the privacy of your living room across Bhilai & Durg.'
  },
  'bundle-complete': {
    title: 'Blishka Complete Head-to-Toe Combo (₹1199) | Full Grooming at Home',
    description: 'Total pampering package at ₹1199. Facial, cleanup, full arms wax, underarms wax, eyebrow, and upper lip in Bhilai and Durg.'
  }
};

/**
 * Resolve SEO metadata for a given route, category, and package
 */
export function getRouteSEO(
  route: string,
  category?: string | null,
  packageId?: string | null
): PageSEOMetadata {
  // Category specific sub-page
  if (route === 'services' && category && CATEGORY_SEO_MAP[category]) {
    const meta = CATEGORY_SEO_MAP[category];
    return {
      title: meta.title,
      description: meta.description,
      keywords: meta.keywords,
      canonicalPath: `/services?category=${category}`
    };
  }

  // Package specific sub-page
  if (route === 'bundles' && packageId && BUNDLE_SEO_MAP[packageId]) {
    const meta = BUNDLE_SEO_MAP[packageId];
    return {
      title: meta.title,
      description: meta.description,
      keywords: 'Beauty packages Bhilai, doorstep parlour combos Durg, salon package offers',
      canonicalPath: `/bundles?package=${packageId}`
    };
  }

  // Top level pages
  switch (route) {
    case 'services':
      return {
        title: 'Doorstep Services & Price Menu | Blishka Bhilai & Durg',
        description: 'Complete doorstep parlour menu for Bhilai & Durg: Waxing, Facials, Cleanups, Manicure, Pedicure, Hair Spa. Transparent salon prices starting at ₹30.',
        keywords: 'Doorstep beauty services Bhilai, home salon price menu Durg, home parlour rate list Bhilai',
        canonicalPath: '/services'
      };

    case 'bundles':
      return {
        title: 'Curated Beauty Bundles & Packages | Blishka Home Parlour',
        description: 'Save up to 30% with curated home beauty packages: Blishka Glow, Everyday, Wax & Smooth, and Self-Care Combos across Bhilai & Durg.',
        keywords: 'Beauty packages Bhilai, parlour combo offers Durg, salon discounts at home',
        canonicalPath: '/bundles'
      };

    case 'cart':
      return {
        title: 'Your Beauty Cart & Booking | Blishka Doorstep Parlour',
        description: 'Review your selected home beauty services, pick your preferred date & time slot, and book certified female specialists in Bhilai & Durg.',
        keywords: 'Book home parlour Bhilai, salon appointment Durg, doorstep beauty booking',
        canonicalPath: '/cart'
      };

    case 'chat':
      return {
        title: 'Blishka AI Beauty Assistant | Chat & Book Doorstep Salon',
        description: 'Chat with Blishka Beauty Assistant for customized skincare recommendations, pricing information, and instant appointment booking in Bhilai & Durg.',
        keywords: 'Beauty assistant Bhilai, salon chatbot Durg, doorstep beauty guidance',
        canonicalPath: '/chat'
      };

    case 'my-bookings':
      return {
        title: 'My Doorstep Bookings | Blishka Customer Portal',
        description: 'Track and manage your upcoming home salon appointments with Blishka across Bhilai and Durg.',
        canonicalPath: '/my-bookings'
      };

    case 'staff':
    case 'partner':
      return {
        title: 'Blishka Staff | Beautician & Partner Duty Portal',
        description: 'Duty jobs, leads dispatch, customer OTP verification, and earnings for Blishka doorstep beauticians and partners.',
        keywords: 'Blishka Staff, Beautician duty app, home parlour partner portal',
        canonicalPath: '/staff'
      };

    case 'super-admin':
      return {
        title: 'Blishka Super Admin | Operations & Financial Management',
        description: 'Executive management console for Blishka: live dispatches, fleet management, catalog control, operational areas, and financial reporting.',
        keywords: 'Blishka Super Admin, salon operations management, doorstep parlour admin console',
        canonicalPath: '/super-admin'
      };

    case 'admin':
      return {
        title: 'Blishka Operations Admin | Live Bookings & Dispatch',
        description: 'Operations console for Blishka: live customer orders and staff dispatch pipeline.',
        keywords: 'Blishka Admin, operations console',
        canonicalPath: '/admin'
      };

    case 'home':
    default:
      return {
        title: 'Blishka | Home Salon & Parlour Services in Bhilai & Durg',
        description: 'Book salon-quality doorstep beauty parlour services in Bhilai & Durg. Certified female beauticians for honey & Rica waxing, VLCC facials, threading, cleanup, manicure & pedicure. Transparent prices starting at ₹30.',
        keywords: 'Blishka, Home Parlour Bhilai, Home Salon Durg, Doorstep Beauty Services Bhilai Durg, Waxing at home Bhilai, Rica wax Bhilai, Facial at home Bhilai, VLCC facial Durg, Manicure pedicure home service Bhilai, Female beautician home visit Bhilai, Parlour services at home Chhattisgarh',
        canonicalPath: '/'
      };
  }
}

/**
 * Apply SEO metadata dynamically to the document head
 */
export function updateSEOMetadata(
  route: string,
  category?: string | null,
  packageId?: string | null
) {
  if (typeof document === 'undefined') return;

  const seo = getRouteSEO(route, category, packageId);

  // 1. Document Title
  document.title = seo.title;

  // Helper to set or create meta tag
  const setMeta = (attrName: string, attrVal: string, content: string) => {
    let el = document.querySelector(`meta[${attrName}="${attrVal}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attrName, attrVal);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  // Helper to set or create link tag
  const setLink = (rel: string, href: string, id?: string) => {
    let el = id ? document.getElementById(id) : document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', rel);
      if (id) el.setAttribute('id', id);
      document.head.appendChild(el);
    }
    el.setAttribute('href', href);
  };

  // 2. Primary Meta Tags
  setMeta('name', 'description', seo.description);
  if (seo.keywords) {
    setMeta('name', 'keywords', seo.keywords);
  }

  // 3. Dynamic PWA WebManifest & Apple Mobile App Identity
  const isStaffRoute = route === 'staff' || route === 'partner';

  let manifestHref = '/manifest.webmanifest';
  let appName = 'Blishka';

  if (isStaffRoute) {
    manifestHref = '/manifest-staff.webmanifest';
    appName = 'Blishka Staff';
  } else if (route === 'super-admin') {
    manifestHref = '/manifest-admin.webmanifest';
    appName = 'Blishka Super Admin';
  } else if (route === 'admin') {
    manifestHref = '/manifest-admin.webmanifest';
    appName = 'Blishka Admin';
  }

  setLink('manifest', manifestHref, 'app-manifest');
  setMeta('name', 'apple-mobile-web-app-title', appName);
  setMeta('name', 'application-name', appName);

  // 4. Canonical Link
  const fullCanonicalUrl = `${BASE_URL}${seo.canonicalPath === '/' ? '' : seo.canonicalPath}`;
  setLink('canonical', fullCanonicalUrl);

  // 5. Open Graph Tags
  setMeta('property', 'og:title', seo.title);
  setMeta('property', 'og:description', seo.description);
  setMeta('property', 'og:url', fullCanonicalUrl);
  setMeta('property', 'og:image', seo.ogImage || DEFAULT_IMAGE);

  // 6. Twitter Card Tags
  setMeta('name', 'twitter:title', seo.title);
  setMeta('name', 'twitter:description', seo.description);
  setMeta('name', 'twitter:image', seo.ogImage || DEFAULT_IMAGE);
}
